/* filepath: packages/volto-castle-theme/src/index.ts */

import type { ConfigType } from '@plone/registry';
import installSettings from './config/settings';

import './theme';
import './static/img/icons/ionicons/css/ionicons.min.css';

import ContactFormBlock from './components/Blocks/ContactForm';
import HPHeroBlock from './components/Blocks/HPHero';
import HPFeaturesBlock from './components/Blocks/HPFeatures';
import HPBestServicesBlock from './components/Blocks/HPBestServices';
import HPLatestPublishedBlock from './components/Blocks/HPLatestPublished';
import HPPressBlock from './components/Blocks/HPPress';
import HPCustomerSupportBlock from './components/Blocks/HPCustomerSupport';
import HPContactEmailBlock from './components/Blocks/HPContactEmail';
import PageLoaderView from './components/PageLoaderView/PageLoaderView';

// App extra: DOM-based placeholder + querystring email prefill + URL cleanup
import VoltoFormPlaceholders from './components/VoltoFormPlaceholders/voltoFormPlaceholders';

// News Item view override (explicitly wired, so you don’t depend on the customizations resolver)
import NewsItemView from './components/NewsItemView/NewsItemView';

const STATIC_PRELOADER_HIDE_DELAY_MS = 5 * 60 * 1000; // 5 minutes (debug)

function initStaticPreloaderHiding(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const hide = () => {
    const el = document.getElementById('preloader');
    if (el) el.style.display = 'none';
  };

  window.addEventListener(
    'load',
    () => window.setTimeout(hide, STATIC_PRELOADER_HIDE_DELAY_MS),
    { once: true },
  );
}

initStaticPreloaderHiding();

function applyConfig(config: ConfigType) {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.wildcardcorpTheme = 'loaded';
  }
  // eslint-disable-next-line no-console
  console.log('[volto-castle-theme] applyConfig running');

  installSettings(config);

  // ------------------------------------------------------------
  // App extras (runs on every route)
  // NOTE: your VoltoFormPlaceholders component should internally
  // scope itself to only run on /contact and /contact-us.
  // ------------------------------------------------------------
  const settingsConfig = config.settings as Record<string, any>;
  settingsConfig.appExtras = [
    ...(settingsConfig.appExtras || []),
    {
      match: '',
      component: VoltoFormPlaceholders,
    },
  ];

  // ------------------------------------------------------------
  // Header / Footer / View wrapper
  // ------------------------------------------------------------
  const componentsConfig = config.components as Record<string, any>;
  componentsConfig.View = { component: PageLoaderView };

  // ------------------------------------------------------------
  // Views: force News Item to use our view component
  // (This guarantees it will render “sidebar fields” on the item page.)
  // ------------------------------------------------------------
  const viewsConfig = config.views as Record<string, any>;
  viewsConfig.contentTypesViews['News Item'] = NewsItemView;

  // ------------------------------------------------------------
  // Register blocks
  // ------------------------------------------------------------
  const blocksConfig = config.blocks.blocksConfig as Record<string, any>;
  blocksConfig.contact_form = ContactFormBlock;
  blocksConfig.hp_hero = HPHeroBlock;
  blocksConfig.hp_features = HPFeaturesBlock;
  blocksConfig.hp_best_services = HPBestServicesBlock;
  blocksConfig.hp_latest_published = HPLatestPublishedBlock;
  blocksConfig.hp_press = HPPressBlock;
  blocksConfig.hp_customer_support = HPCustomerSupportBlock;
  blocksConfig.hp_contact_email = HPContactEmailBlock;

  // Register block groups
  config.blocks.groupBlocksOrder = [
    ...(config.blocks.groupBlocksOrder || []),
    { id: 'homepage', title: 'Home Page' },
  ];

  return config;
}

export default applyConfig;